import { Fragment, useState, useRef, useCallback, useEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import {
  Plus,
  Paperclip,
  Wand2,
  BookOpen,
  PenLine,
  HardDrive,
  Zap,
  Brain,
  Handshake,
  Bot,
  ArrowUp,
  ArrowRight,
  Camera,
  Square,
  Loader2,
  X,
} from "lucide-react"
import labsLogo from "@/assets/44 Labs Logos/44LABS_webclip_black_256x256.png"
import { VoiceInput } from "../chat-ui/VoiceInput"
import { FileAttachment } from "../chat-ui/FileAttachment"
import { ORCA_MODES_ORDERED, type OrcaModeId } from "@/config/orcaModes"
import { ORCA_ROUTING_CREDIT_COPY } from "@/config/orcaRoutingCreditCopy"
import { PROMPT_CATEGORIES } from "@/config/promptTemplates"
import { useIsMobile } from "@/hooks/use-mobile"
import { SynthesisIcon } from "../icons/LayerIcons"
import { PromptLibraryModal } from "./PromptLibraryModal"
import {
  ConnectedDirectiveGuidance,
  type ConnectedDirectiveState,
} from "./ConnectedDirectiveGuidance"

interface ActionCardData {
  id: string
  title: string
  description: string
  priority?: boolean
  actionType?: string
}

interface TabChatTab {
  id: string
  label: string
}

interface ActiveSmartTool {
  id: string
  title: string
  creditCost?: number | null
  quoteStatus?: "idle" | "loading" | "ready" | "error" | "blocked"
  quoteMessage?: string | null
}

interface ChatInputProps {
  variant: "discovery" | "active"
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onStop?: () => void
  isLoading: boolean
  isStreaming?: boolean
  disabled?: boolean
  userName?: string
  suggestions?: string[]
  onSuggestionClick?: (s: string) => void
  directive?: ConnectedDirectiveState | null
  onDirectiveAction?: (label: string) => void
  onPromptSelect?: (prompt: string) => void
  onQuickAction?: (action: string) => void
  actionCards?: ActionCardData[]
  onActionCardClick?: (id: string) => void
  selectedActionCardId?: string | null
  actionCardBusyId?: string | null
  actionCardOfferActive?: boolean
  onActionCardDismiss?: (id: string) => void
  tabChatActive?: boolean
  tabChatTabs?: TabChatTab[]
  selectedTabs?: string[]
  onToggleTab?: (tabId: string) => void
  artifactActive?: boolean
  artifactDismissed?: boolean
  onArtifactConfirm?: () => void
  onArtifactDismiss?: () => void
  /**
   * §7.2.5 Discovery Ladder — Card 2 Layer A "The Offer."
   * When an artifact offer is active, render orange CTA (primaryArtifact.label)
   * + "Not now" buttons inside the directive zone. The directive paragraph
   * (directive.text) already contains the phase-grounded offer sentence.
   */
  artifactOffer?: {
    primaryArtifact: { type: string; label: string; prompt: string }
    secondaryArtifact?: { type: string; label: string; prompt: string } | null
  } | null
  onOfferConfirm?: (which: "primary" | "secondary") => void
  onOfferDismiss?: () => void
  orcaMode?: string
  onModeChange?: (mode: string) => void
  /** Mode that Cortex resolved to (from routing_decision SSE). Shown as "active" in selector. */
  resolvedMode?: string
  compact?: boolean
  /** Compact styling for action cards (discovery variant only). Independent of `compact` which governs pills. */
  compactCards?: boolean
  /** Presents the discovery composer and live action cards as the full ORCA Directive Surface. */
  directiveSurface?: boolean
  /** Bearing mobile keeps its prompt launcher in the persistent composer sheet. */
  useSheetPromptLauncher?: boolean
  /** Renders only the real ChatPane-owned composer for the persistent mobile sheet. */
  sheetComposer?: boolean
  /** Mobile layout host for this same ChatInput-owned directive presentation. */
  sheetDirectiveHost?: HTMLElement | null
  stagedFileCount?: number
  stagedFiles?: Array<{ id: string; file: File; previewUrl?: string }>
  onRemoveStagedFile?: (id: string) => void
  isUploadingFiles?: boolean
  uploadError?: string | null
  onClearUploadError?: () => void
  activeSmartTool?: ActiveSmartTool | null
  onClearSmartTool?: () => void
}

const ICON_MAP = { Zap, Brain, Handshake, Bot } as const

const DIRECTIVE_CARD_SLOTS: Record<string, { label: string; slot: string }> = {
  review: { label: "Next Action", slot: "next-action" },
  create: { label: "Create Artifact", slot: "create-artifact" },
  agent_activation: { label: "Activate Agent", slot: "activate-agent" },
}

const HIDDEN_DIRECTIVE_SURFACE_CARD_IDS = new Set(["create", "agent_activation"])

function getDirectiveCardPresentation(card: ActionCardData) {
  const slot = DIRECTIVE_CARD_SLOTS[card.id]
  if (!slot) {
    return {
      label: card.title,
      description: card.description,
      slot: card.id,
    }
  }

  const description = card.description.replace(/\s+/g, " ").trim()
  const liveTitle = card.title.trim()
  return {
    ...slot,
    description: card.id === "review" && liveTitle !== slot.label
      ? `${liveTitle}. ${description}`
      : description,
  }
}

const MODES = ORCA_MODES_ORDERED.map((m) => ({
  id: m.id as OrcaModeId,
  label: m.label,
  desc: m.description,
  icon: ICON_MAP[m.iconName],
}))

export const ROUTING_DISCLOSURE_COPY = {
  idleLabel: "Routing",
  activePrefix: "Routed to",
  desktopBanner: "Orca routes every message automatically",
  mobileBanner: "Orca routes every message automatically",
  sectionLabel: "Possible routing paths",
} as const

const ROUTING_PATHS = MODES.filter((mode) => mode.id !== "auto")

const MODE_TIER: Record<string, number> = Object.fromEntries(
  ORCA_MODES_ORDERED.map((m) => [m.id, m.tier])
)

export const DIRECTIVE_COMPACT_WIDTH = 520

export function isCompactDirectiveWidth(width: number) {
  return width <= DIRECTIVE_COMPACT_WIDTH
}

export function getTimeOfDayGreeting(date = new Date()) {
  const hour = date.getHours()
  if (hour < 12) return "Good morning"
  if (hour < 18) return "Good afternoon"
  return "Good evening"
}

interface RoutingDisclosureModalProps {
  open: boolean
  isMobile: boolean
  resolvedMode?: string
  onClose: () => void
}

function RoutingDisclosureModal({
  open,
  isMobile,
  resolvedMode,
  onClose,
}: RoutingDisclosureModalProps) {
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, onClose])

  if (!open || typeof document === "undefined") return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        padding: isMobile ? 16 : 24,
        background: "rgba(5, 7, 5, 0.66)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="orca-routing-modal-title"
        className="relative"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? "100%" : "min(760px, calc(100vw - 48px))",
          maxHeight: isMobile ? "calc(100svh - 32px)" : "calc(100vh - 96px)",
          overflowY: "auto",
          padding: isMobile ? 18 : 24,
          borderRadius: 16,
          border: "1px solid var(--orca-border-strong)",
          background: "var(--orca-bg-deep)",
          boxShadow: "0 34px 96px rgba(0, 0, 0, 0.68), 0 0 0 1px rgba(255, 255, 255, 0.08)",
          fontFamily: "var(--orca-font-body)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--orca-accent-soft)",
                flexShrink: 0,
              }}
            >
              <Zap size={16} style={{ color: "var(--orca-accent)" }} />
            </div>
            <div>
              <h2
                id="orca-routing-modal-title"
                style={{
                  margin: 0,
                  color: "var(--orca-text-primary)",
                  fontFamily: "var(--orca-font-body)",
                  fontSize: 18,
                  fontWeight: 650,
                  lineHeight: 1.25,
                }}
              >
                {ROUTING_DISCLOSURE_COPY.desktopBanner}
              </h2>
              <p
                style={{
                  margin: "6px 0 0",
                  color: "var(--orca-text-muted)",
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                Orca decides which path fits each message. These paths explain what can happen behind the scenes.
              </p>
            </div>
          </div>
          <button
            aria-label="Close routing details"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              border: "1px solid var(--orca-border-subtle)",
              background: "var(--orca-bg-elevated)",
              color: "var(--orca-text-muted)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </div>
        <div style={{
          padding: "0 0 10px",
          fontSize: 11,
          color: "var(--orca-text-muted)",
          fontFamily: "var(--orca-font-mono)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}>
          {ROUTING_DISCLOSURE_COPY.sectionLabel}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${ROUTING_PATHS.length}, 1fr)`, gap: 8 }}>
          {ROUTING_PATHS.map((mode) => {
            const isResolved = resolvedMode === mode.id
            return (
              <div
                key={mode.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: 14,
                  borderRadius: 10,
                  border: isResolved
                    ? "1px solid var(--orca-accent)"
                    : "1px solid var(--orca-border-subtle)",
                  background: "var(--orca-bg-elevated)",
                  cursor: "default",
                  textAlign: "left",
                  transition: "border-color 0.15s, background 0.15s",
                  position: "relative",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <mode.icon size={15} style={{ color: isResolved ? "var(--orca-accent)" : "var(--orca-text-muted)", flexShrink: 0 }} />
                  {isResolved && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--orca-accent)", fontFamily: "var(--orca-font-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Active</span>
                  )}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--orca-text-primary)", fontFamily: "var(--orca-font-body)", lineHeight: 1.2 }}>
                  {mode.label}
                </span>
                <span style={{ fontSize: 12.5, color: "var(--orca-text-secondary)", fontFamily: "var(--orca-font-body)", lineHeight: 1.45 }}>
                  {mode.desc}
                </span>
                <span style={{ fontSize: 12, color: "var(--orca-text-muted)", fontFamily: "var(--orca-font-body)", lineHeight: 1.45 }}>
                  {ORCA_ROUTING_CREDIT_COPY[mode.id as keyof typeof ORCA_ROUTING_CREDIT_COPY].shortCredit}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}

const PLUS_ITEMS = [
  { icon: Paperclip, label: "Add photos and files", action: "attach" },
  { icon: Wand2, label: "Create an artifact", action: "artifact" },
  { icon: BookOpen, label: "Study and learn", action: "study" },
  { icon: PenLine, label: "Draw a sketch", action: "sketch" },
  { icon: HardDrive, label: "Add from Drive", action: "drive" },
] as const

export function ChatInput({
  variant,
  value,
  onChange,
  onSubmit,
  onStop,
  isLoading,
  isStreaming = false,
  disabled = false,
  userName,
  suggestions,
  onSuggestionClick,
  directive,
  onDirectiveAction,
  onPromptSelect,
  onQuickAction,
  actionCards,
  onActionCardClick,
  selectedActionCardId,
  actionCardBusyId,
  actionCardOfferActive = false,
  onActionCardDismiss,
  tabChatActive,
  tabChatTabs,
  selectedTabs,
  onToggleTab,
  artifactActive,
  artifactDismissed,
  onArtifactConfirm,
  onArtifactDismiss,
  artifactOffer,
  onOfferConfirm,
  onOfferDismiss,
  orcaMode = "auto",
  onModeChange,
  resolvedMode,
  compact = false,
  compactCards = false,
  directiveSurface = false,
  useSheetPromptLauncher = false,
  sheetComposer = false,
  sheetDirectiveHost = null,
  stagedFileCount = 0,
  stagedFiles = [],
  onRemoveStagedFile,
  isUploadingFiles = false,
  uploadError,
  onClearUploadError,
  activeSmartTool,
  onClearSmartTool,
}: ChatInputProps) {
  const isMobile = useIsMobile()
  const [modeMenuOpen, setModeMenuOpen] = useState(false)
  const [plusMenuOpen, setPlusMenuOpen] = useState(false)
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false)
  const [fullPromptLibraryOpen, setFullPromptLibraryOpen] = useState(false)
  const [promptCategory, setPromptCategory] = useState<string | null>(null)
  const [directiveLaneCompact, setDirectiveLaneCompact] = useState(false)
  const promptLibraryRef = useRef<HTMLDivElement>(null)
  const promptLauncherRef = useRef<HTMLButtonElement>(null)
  const promptSelectedRef = useRef(false)
  const plusButtonRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const directiveSurfaceRef = useRef<HTMLDivElement>(null)
  const [sheetPlusMenuPosition, setSheetPlusMenuPosition] = useState<{
    left: number
    bottom: number
  } | null>(null)

  const hasSendableContent = value.trim().length > 0 || stagedFileCount > 0
  const hasTypedValue = value.trim().length > 0
  const standaloneDirectiveText = directive?.text.replace(/\u200B/g, "").trim() || ""
  const composerEngaged = hasSendableContent
  const smartToolReady = !activeSmartTool || activeSmartTool.quoteStatus === "ready"
  const smartToolSubmitBlocked = hasSendableContent && !smartToolReady && !isStreaming && !isLoading
  const canSend = hasSendableContent && smartToolReady && !isStreaming
  const activeSmartToolQuoteLabel = activeSmartTool
    ? activeSmartTool.quoteStatus === "loading"
      ? "Checking credits..."
      : activeSmartTool.quoteStatus === "ready" && activeSmartTool.creditCost != null
      ? `Generating will use ${activeSmartTool.creditCost} credits`
      : activeSmartTool.quoteMessage || "Credit quote unavailable"
    : null

  // Listen for orca:focusChatInput event (from action card "Ask Orca" click)
  useEffect(() => {
    const handler = () => textareaRef.current?.focus()
    window.addEventListener("orca:focusChatInput", handler)
    return () => window.removeEventListener("orca:focusChatInput", handler)
  }, [])

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "24px"
    el.style.height = Math.min(el.scrollHeight, 160) + "px"
  }, [value])

  useEffect(() => {
    if (!directiveSurface) {
      setDirectiveLaneCompact(false)
      return
    }

    const surface = directiveSurfaceRef.current
    if (!surface) return

    const syncCompactState = (width: number) => {
      const nextCompact = isCompactDirectiveWidth(width)
      setDirectiveLaneCompact((current) => current === nextCompact ? current : nextCompact)
    }

    syncCompactState(surface.getBoundingClientRect().width)

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) syncCompactState(entry.contentRect.width)
      })
      observer.observe(surface)
      return () => observer.disconnect()
    }

    const handleResize = () => syncCompactState(surface.getBoundingClientRect().width)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [directiveSurface])

  useEffect(() => {
    if (!modeMenuOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        setModeMenuOpen(false)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [modeMenuOpen])

  useEffect(() => {
    if (!plusMenuOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        setPlusMenuOpen(false)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [plusMenuOpen])

  // Close prompt library on outside click
  useEffect(() => {
    if (!promptLibraryOpen) return
    const handler = (e: MouseEvent) => {
      if (promptLibraryRef.current && !promptLibraryRef.current.contains(e.target as Node)) {
        setPromptLibraryOpen(false)
        setPromptCategory(null)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [promptLibraryOpen])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        if (canSend) onSubmit()
      }
    },
    [canSend, onSubmit]
  )

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value)
      const el = e.target
      el.style.height = "24px"
      el.style.height = Math.min(el.scrollHeight, 160) + "px"
    },
    [onChange]
  )

  // Read-only disclosure: users do not select modes. Orca always routes
  // automatically; this button explains the route that ran.
  const displayMode = resolvedMode
    ? MODES.find((m) => m.id === resolvedMode) || MODES[0]
    : MODES[0]
  const displayModeLabel = resolvedMode
    ? `${ROUTING_DISCLOSURE_COPY.activePrefix} ${displayMode.label.replace(/^Orca\s+/, "")}`
    : ROUTING_DISCLOSURE_COPY.idleLabel

  const openFullPromptLibrary = useCallback(() => {
    setPromptLibraryOpen(false)
    setPromptCategory(null)
    setFullPromptLibraryOpen(true)
  }, [])

  // The mobile Home landing keeps this existing library as the only prompt
  // source. A small shell control can request it without creating a second
  // prompt picker or another composer owner.
  useEffect(() => {
    const handler = () => openFullPromptLibrary()
    window.addEventListener("orca:openPromptLibrary", handler)
    return () => window.removeEventListener("orca:openPromptLibrary", handler)
  }, [openFullPromptLibrary])

  const handleFullPromptLibraryOpenChange = useCallback((open: boolean) => {
    setFullPromptLibraryOpen(open)
    if (!open) {
      const shouldFocusComposer = promptSelectedRef.current
      promptSelectedRef.current = false
      window.requestAnimationFrame(() => {
        if (shouldFocusComposer) {
          textareaRef.current?.focus()
        } else {
          const returnTarget = sheetComposer ? plusButtonRef.current : promptLauncherRef.current
          returnTarget?.focus()
        }
      })
    }
  }, [sheetComposer])

  const handlePromptSelect = useCallback(
    (prompt: string) => {
      promptSelectedRef.current = true
      onPromptSelect?.(prompt)
      setPromptLibraryOpen(false)
      setPromptCategory(null)
      setFullPromptLibraryOpen(false)
    },
    [onPromptSelect]
  )

  const sheetPlusItems = [
    PLUS_ITEMS[0],
    { icon: Camera, label: "Take a photo", action: "camera" as const },
    { icon: displayMode.icon, label: displayModeLabel, action: "routing" as const },
    { icon: SynthesisIcon, label: "ADU Prompts", action: "prompts" as const },
  ]
  const plusItems = sheetComposer ? sheetPlusItems : PLUS_ITEMS
  const sheetMenuPortalTarget = sheetComposer && typeof document !== "undefined"
    ? (textareaRef.current?.closest(".bearing-global-composer-layer") as HTMLElement | null)
    : null

  const renderPlusMenuItems = () => plusItems.map((item) => (
    <Fragment key={item.label}>
      {sheetComposer && item.action === "routing" && (
        <div className="orca-popover-separator bearing-menu-popover-separator" role="separator" />
      )}
      <button
        className="orca-popover-item bearing-menu-popover-item"
        role="menuitem"
        onClick={() => {
          setPlusMenuOpen(false)
          if (item.action === "prompts") {
            openFullPromptLibrary()
            return
          }
          if (item.action === "routing") {
            setModeMenuOpen(true)
            return
          }
          onQuickAction?.(item.action)
        }}
      >
        <item.icon size={16} aria-hidden="true" />
        <span>{item.label}</span>
      </button>
    </Fragment>
  ))

  const inlineDirectiveVisible = Boolean(directive?.text && !sheetComposer)
  const connectedDirectiveGuidance = directive?.text ? (
    <ConnectedDirectiveGuidance
      directive={directive}
      presentation={sheetComposer ? "sheet" : "inline"}
      isUpdating={Boolean(actionCardBusyId)}
      brandIconSrc={sheetComposer ? labsLogo : undefined}
      showLabel={directiveSurface}
    />
  ) : null

  // ─── SHARED PROMPT CARD (used by both discovery + active) ───
  const promptCard = (
    <div
      className={`orca-console-prompt ${variant === "active" ? "active" : "discovery"}${inlineDirectiveVisible ? " has-directive" : ""}${directiveSurface ? " orca-connected-directive-composer" : ""}${sheetComposer ? " bearing-sheet-runtime-prompt" : ""}`}
      data-connected-directive-composer-part={sheetComposer ? "composer" : undefined}
      {...(directiveSurface
        ? {
            role: "group",
            "aria-label": "Connected Directive Composer",
            "aria-busy": Boolean(actionCardBusyId),
          }
        : {})}
    >
      {inlineDirectiveVisible && directive && (
        <div className="orca-directive-zone">
          {connectedDirectiveGuidance}
          {tabChatActive && !directive?.isTyping && tabChatTabs && tabChatTabs.length > 0 && (
            <div className="orca-directive-tabs">
              {tabChatTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`orca-tab-select-pill ${selectedTabs?.includes(tab.id) ? "selected" : ""}`}
                  onClick={() => onToggleTab?.(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          {artifactActive && !artifactDismissed && !directive?.isTyping && (
            <div className="orca-artifact-confirm">
              <button
                className="orca-artifact-confirm-btn secondary"
                onClick={onArtifactDismiss}
              >
                Not right now
              </button>
              <button
                className="orca-artifact-confirm-btn primary"
                onClick={onArtifactConfirm}
              >
                Yes, create my budget
              </button>
            </div>
          )}
          {/* §7.2.5 Discovery Ladder — Card 2 Layer A offer affordance.
              Single-artifact decision pattern: AI commits to ONE grounded
              artifact (the primary), user accepts or defers. Asymmetric
              visual weight — orange CTA vs. tertiary text dismiss — keeps
              the confirm action unambiguous. */}
          {artifactOffer?.primaryArtifact && !directive?.isTyping && (
            <div className="orca-artifact-confirm">
              <button
                className="orca-artifact-confirm-btn not-now"
                onClick={() => onOfferDismiss?.()}
              >
                Not now
              </button>
              <button
                className="orca-artifact-confirm-btn primary"
                onClick={() => onOfferConfirm?.("primary")}
              >
                {artifactOffer.primaryArtifact.label}
              </button>
            </div>
          )}
        </div>
      )}

      <div className={`orca-console-input-area${composerEngaged ? " is-typing" : ""}`}>
        {/* Staged file previews — above textarea, inside input card */}
        {uploadError && (
          <div className="mx-4 mt-3 mb-1 flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: 'color-mix(in srgb, var(--orca-error) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--orca-error) 30%, transparent)', color: 'var(--orca-error)' }}>
            <span className="material-icons text-sm">error</span>
            <span className="flex-1">{uploadError}</span>
            <button onClick={onClearUploadError} className="material-icons text-sm hover:opacity-70 cursor-pointer">close</button>
          </div>
        )}
        {stagedFiles.length > 0 && (
          <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2">
            {stagedFiles.map((sf) => (
              <div key={sf.id} className="relative group">
                <FileAttachment
                  attachment={{
                    fileName: sf.file.name,
                    fileType: sf.file.type,
                    fileSize: sf.file.size,
                    downloadUrl: sf.previewUrl || "",
                    previewUrl: sf.previewUrl,
                  }}
                  compact
                />
                <button
                  onClick={() => onRemoveStagedFile?.(sf.id)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--orca-error)] text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  &times;
                </button>
              </div>
            ))}
            {isUploadingFiles && (
              <div className="inline-flex items-center gap-2 px-2.5 py-2 bg-[var(--orca-bg-surface)] border border-[var(--orca-border-subtle)] rounded-[var(--orca-radius-sm)] text-[11px] text-[var(--orca-text-muted)]">
                <span className="material-icons text-sm animate-spin">sync</span>
                <span>Uploading...</span>
              </div>
            )}
          </div>
        )}
        <div className="orca-input-row px-5 pt-2.5 pb-1 relative">
          {!value && <div className="orca-input-caret" />}
          <textarea
            ref={textareaRef}
            className="w-full border-none bg-transparent outline-none resize-none text-base leading-snug min-h-[28px] max-h-[200px] pr-7"
            style={{
              fontFamily: "var(--orca-font-body)",
              color: "var(--orca-text-primary)",
            }}
            placeholder={sheetComposer ? "Ask Orca anything..." : "Ask ORCA anything..."}
            aria-label={sheetComposer ? "Ask Orca anything" : "Ask ORCA anything"}
            rows={1}
            value={value}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
          {(directiveSurface || hasTypedValue) && (
            <button
              onClick={() => onChange("")}
              className="orca-input-clear absolute top-3 right-4 w-[22px] h-[22px] rounded-full bg-[var(--orca-bg-elevated)] border border-[var(--orca-border-medium)] text-[var(--orca-text-muted)] flex items-center justify-center cursor-pointer hover:text-[var(--orca-text-primary)] transition-all"
              aria-label="Clear message"
              aria-hidden={!hasTypedValue}
              tabIndex={hasTypedValue ? 0 : -1}
              style={{ pointerEvents: hasTypedValue ? undefined : "none" }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {activeSmartTool && (
          <div className="px-4 pt-1 pb-1">
            <div
              className="flex items-center gap-2 rounded-lg border px-2.5 py-2"
              style={{
                background: "color-mix(in srgb, var(--orca-accent) 6%, var(--orca-bg-card))",
                borderColor: "color-mix(in srgb, var(--orca-accent) 28%, var(--orca-border-subtle))",
              }}
            >
              <span
                className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                style={{
                  color: "var(--orca-accent)",
                  background: "var(--orca-bg-surface)",
                  border: "1px solid color-mix(in srgb, var(--orca-accent) 22%, transparent)",
                  fontFamily: "var(--orca-font-mono)",
                }}
              >
                Smart Tool
              </span>
              <span
                className="min-w-0 flex-1 truncate text-[12px] font-semibold"
                style={{ color: "var(--orca-text-primary)" }}
              >
                {activeSmartTool.title}
              </span>
              <span
                className="text-[11px] font-medium"
                style={{ color: "var(--orca-text-muted)" }}
              >
                {activeSmartToolQuoteLabel}
              </span>
              <button
                type="button"
                onClick={onClearSmartTool}
                aria-label="Remove Smart Tool"
                title="Remove Smart Tool"
                className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors"
                style={{
                  color: "var(--orca-text-muted)",
                  border: "1px solid var(--orca-border-subtle)",
                  background: "var(--orca-bg-surface)",
                }}
              >
                <X size={13} />
              </button>
            </div>
          </div>
        )}

        <div className="orca-input-bar flex items-center justify-between px-4 py-2.5 gap-3">
          {/* Left: Plus + Mode */}
          <div className="orca-bar-side flex items-center gap-2">
            <div className="relative orca-plus-wrap">
              <button
                ref={plusButtonRef}
                className={`orca-console-action-btn icon-only ${plusMenuOpen ? "active" : ""}`}
                type="button"
                aria-label={sheetComposer ? "More options" : "Add context"}
                aria-haspopup="menu"
                aria-expanded={plusMenuOpen}
                onClick={() => {
                  if (!plusMenuOpen && sheetComposer && plusButtonRef.current) {
                    const rect = plusButtonRef.current.getBoundingClientRect()
                    setSheetPlusMenuPosition({
                      left: rect.left,
                      bottom: window.innerHeight - rect.top + 8,
                    })
                  }
                  setPlusMenuOpen((open) => !open)
                }}
              >
                <Plus size={18} />
              </button>
              {plusMenuOpen && sheetComposer && sheetMenuPortalTarget && sheetPlusMenuPosition
                ? createPortal(
                  <>
                    <button
                      type="button"
                      className="bearing-sheet-runtime-menu-backdrop"
                      aria-label="Close options menu"
                      onClick={() => setPlusMenuOpen(false)}
                    />
                    <div
                      className="orca-popover bearing-menu-popover bearing-sheet-runtime-popover"
                      role="menu"
                      aria-label="More options"
                      style={{
                        left: sheetPlusMenuPosition.left,
                        bottom: sheetPlusMenuPosition.bottom,
                      }}
                    >
                      {renderPlusMenuItems()}
                    </div>
                  </>,
                  sheetMenuPortalTarget,
                )
                : plusMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setPlusMenuOpen(false)}
                  />
                  <div className="orca-popover" role="menu" aria-label="Add to conversation">
                    {renderPlusMenuItems()}
                  </div>
                </>
              )}
            </div>

            {!sheetComposer && <div className="relative">
              <button
                className={`orca-console-action-btn${compact && !directiveSurface ? " icon-only" : ""} ${modeMenuOpen || resolvedMode ? "active" : ""}`}
                type="button"
                aria-label="Open routing details"
                onClick={() => setModeMenuOpen((o) => !o)}
              >
                <displayMode.icon size={compact && !directiveSurface ? 18 : 14} style={{ color: "var(--orca-accent)" }} />
                {(!compact || directiveSurface) && <span>{displayModeLabel}</span>}
              </button>
              <RoutingDisclosureModal
                open={modeMenuOpen}
                isMobile={isMobile}
                resolvedMode={resolvedMode}
                onClose={() => setModeMenuOpen(false)}
              />
            </div>}
          </div>

          {/* Right: desktop/legacy Prompts + Mic + Send. Bearing mobile keeps
              its prompt launcher in the persistent composer sheet. */}
          <div className="orca-bar-side flex items-center gap-2">
            {!sheetComposer &&
              (!isMobile || !useSheetPromptLauncher) &&
              (!canSend || directiveSurface) &&
              !isStreaming &&
              !isLoading &&
              onPromptSelect && (
              <div className="relative orca-prompts-wrap" ref={promptLibraryRef}>
                <button
                  ref={promptLauncherRef}
                  className={`orca-console-action-btn${compact && !directiveSurface ? " icon-only" : " orca-prompts-btn"}${promptLibraryOpen ? " active" : ""}`}
                  type="button"
                  aria-label="Open prompt library"
                  aria-hidden={composerEngaged}
                  tabIndex={composerEngaged ? -1 : 0}
                  aria-haspopup={isMobile ? "dialog" : "menu"}
                  aria-expanded={isMobile ? fullPromptLibraryOpen : promptLibraryOpen}
                  onClick={() => {
                    if (isMobile) {
                      openFullPromptLibrary()
                      return
                    }
                    setPromptLibraryOpen(!promptLibraryOpen)
                    setPromptCategory(null)
                  }}
                >
                  <SynthesisIcon size={16} />
                  {(!compact || directiveSurface) && <span>Prompts</span>}
                </button>
                {/* Desktop: positioned popover */}
                {promptLibraryOpen && onPromptSelect && !isMobile && (
                  <div className="absolute bottom-full right-0 mb-2 w-80 max-h-[360px] overflow-y-auto bg-[var(--orca-bg-elevated)] border border-[var(--orca-border-medium)] rounded-[var(--orca-radius-md)] shadow-lg z-50">
                    <div className="px-3 py-2 border-b border-[var(--orca-border-subtle)]">
                      <span className="text-xs font-medium text-[var(--orca-text-primary)]">
                        {promptCategory
                          ? PROMPT_CATEGORIES.find((c) => c.id === promptCategory)?.label
                          : "Prompts"}
                      </span>
                      {promptCategory && (
                        <button
                          className="ml-2 text-[10px] text-[var(--orca-accent)] hover:underline"
                          onClick={() => setPromptCategory(null)}
                        >
                          Back
                        </button>
                      )}
                    </div>
                    {!promptCategory ? (
                      <div className="p-1.5">
                        {PROMPT_CATEGORIES.map((cat) => (
                          <button
                            key={cat.id}
                            className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-[var(--orca-radius-sm)] text-left transition-colors hover:bg-[var(--orca-bg-card-hover)]"
                            onClick={() => setPromptCategory(cat.id)}
                          >
                            <span className="material-icons text-base text-[var(--orca-accent)]" style={{ fontSize: "18px" }}>
                              {cat.icon}
                            </span>
                            <div>
                              <div className="text-xs font-medium text-[var(--orca-text-primary)]">{cat.label}</div>
                              <div className="text-[10px] text-[var(--orca-text-muted)]">{cat.prompts.length} prompts</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-1.5">
                        {PROMPT_CATEGORIES.find((c) => c.id === promptCategory)?.prompts.map((p) => (
                          <button
                            key={p.id}
                            className="flex items-start gap-2 w-full px-2.5 py-2 rounded-[var(--orca-radius-sm)] text-left transition-colors hover:bg-[var(--orca-bg-card-hover)] group"
                            onClick={() => {
                              handlePromptSelect(p.text)
                            }}
                          >
                            <span className="material-icons text-sm text-[var(--orca-text-muted)] mt-0.5 group-hover:text-[var(--orca-accent)] transition-colors" style={{ fontSize: "14px" }}>
                              arrow_forward
                            </span>
                            <div>
                              <div className="text-xs text-[var(--orca-text-primary)]">{p.label}</div>
                              <div className="text-[10px] text-[var(--orca-text-muted)] mt-0.5 line-clamp-2">{p.text}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-[var(--orca-border-subtle)] p-1.5">
                      <button
                        className="flex items-center justify-center gap-1.5 w-full px-2.5 py-2 rounded-[var(--orca-radius-sm)] text-xs font-medium text-[var(--orca-accent)] transition-colors hover:bg-[var(--orca-accent-soft)]"
                        onClick={openFullPromptLibrary}
                      >
                        <SynthesisIcon size={14} />
                        Browse full prompt library
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {(canSend || isStreaming || isLoading) && (
              <VoiceInput
                className={directiveSurface ? "orca-voice-input orca-slot-mic" : sheetComposer ? "bearing-sheet-runtime-voice" : undefined}
                onTranscript={(text) => {
                  onChange((value + " " + text).trim())
                }}
              />
            )}
            {isStreaming ? (
              <button
                className="orca-console-submit bg-[var(--orca-bg-elevated)] text-[var(--orca-text-primary)] border border-[var(--orca-border-medium)]"
                onClick={onStop}
                aria-label="Stop response"
              >
                <Square size={12} fill="currentColor" />
              </button>
            ) : isLoading ? (
              <button
                className="orca-console-submit bg-[var(--orca-accent-soft)] text-[var(--orca-accent)] border border-[rgba(232,93,38,0.2)]"
                disabled
                aria-label="Sending message"
              >
                <Loader2 size={16} className="orca-spin" />
              </button>
            ) : canSend ? (
              <button className="orca-console-submit" onClick={onSubmit} aria-label="Send message">
                <ArrowUp size={18} />
              </button>
            ) : smartToolSubmitBlocked ? (
              <button
                className="orca-console-submit bg-[var(--orca-bg-elevated)] text-[var(--orca-text-muted)] border border-[var(--orca-border-medium)]"
                disabled
                aria-label="Smart Tool unavailable"
                title={activeSmartToolQuoteLabel || "Smart Tool unavailable"}
              >
                <ArrowUp size={18} />
              </button>
            ) : (
              <VoiceInput
                className={directiveSurface ? "orca-voice-input" : sheetComposer ? "bearing-sheet-runtime-voice" : undefined}
                onTranscript={(text) => {
                  onChange(text)
                }}
              />
            )}
          </div>
        </div>
      </div>

      {onPromptSelect && fullPromptLibraryOpen && (
        <PromptLibraryModal
          open={fullPromptLibraryOpen}
          onOpenChange={handleFullPromptLibraryOpenChange}
          onPromptSelect={handlePromptSelect}
        />
      )}
      {sheetComposer && (
        <RoutingDisclosureModal
          open={modeMenuOpen}
          isMobile
          resolvedMode={resolvedMode}
          onClose={() => setModeMenuOpen(false)}
        />
      )}
    </div>
  )

  if (sheetComposer) {
    return (
      <>
        <div className="bearing-sheet-runtime-composer" role="group" aria-label="Orca chat composer">
          {promptCard}
        </div>
        {sheetDirectiveHost && connectedDirectiveGuidance
          ? createPortal(connectedDirectiveGuidance, sheetDirectiveHost)
          : null}
      </>
    )
  }

  // ─── DISCOVERY MODE ───
  if (variant === "discovery") {
    const visibleActionCards = directiveSurface
      ? actionCards?.filter((card) => !isMobile && !HIDDEN_DIRECTIVE_SURFACE_CARD_IDS.has(card.id))
      : actionCards
    const discoveryChoices = visibleActionCards && visibleActionCards.length > 0 ? (
      <div
        className={`orca-action-cards${directiveSurface ? " below-prompt" : ""}${compactCards ? " compact" : ""}${directiveSurface && (actionCardOfferActive || actionCardBusyId) ? " offer-mode" : ""}`}
        {...(directiveSurface ? { role: "group", "aria-label": "ORCA Directive Cards" } : {})}
      >
        {visibleActionCards.map((card) => {
          const presentation = directiveSurface
            ? getDirectiveCardPresentation(card)
            : { label: card.title, description: card.description, slot: card.id }
          const legacyActive = Boolean(
            (tabChatActive && card.id === "tab_chat") ||
              (artifactActive && card.id === "create"),
          )
          const isActive = selectedActionCardId != null
            ? selectedActionCardId === card.id
            : legacyActive
          const isBusy = actionCardBusyId === card.id

          if (directiveSurface) {
            return (
              <div
                key={card.id}
                className={`orca-action-card ${card.priority ? "priority" : ""} ${isActive ? "active" : ""}`}
                data-directive-slot={presentation.slot}
                data-state={isActive ? "selected" : "resting"}
              >
                <button
                  type="button"
                  className="orca-action-card-trigger"
                  onClick={() => onActionCardClick?.(card.id)}
                  aria-pressed={isActive}
                  aria-busy={isBusy}
                >
                  <span className="orca-action-card-title">{presentation.label}</span>
                  <span className="orca-action-card-desc">{presentation.description}</span>
                  <span className="orca-action-card-go" aria-hidden="true">
                    <ArrowRight size={15} />
                  </span>
                </button>
                {onActionCardDismiss && (
                  <button
                    type="button"
                    className="orca-action-card-dismiss"
                    aria-label={`Dismiss ${presentation.label} selection`}
                    aria-hidden={!isActive}
                    tabIndex={isActive ? 0 : -1}
                    disabled={!isActive}
                    onClick={() => isActive && onActionCardDismiss(card.id)}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            )
          }

          return (
            <button
              key={card.id}
              type="button"
              className={`orca-action-card ${card.priority ? "priority" : ""} ${isActive ? "active" : ""}`}
              onClick={() => onActionCardClick?.(card.id)}
            >
              <div className="orca-action-card-title">{presentation.label}</div>
              <div className="orca-action-card-desc">{presentation.description}</div>
              {directiveSurface && (
                <span className="orca-action-card-go" aria-hidden="true">
                  <ArrowRight size={15} />
                </span>
              )}
            </button>
          )
        })}
      </div>
    ) : suggestions && suggestions.length > 0 ? (
      <div className="orca-console-suggestions">
        {suggestions.map((s) => (
          <button
            key={s}
            className="orca-suggestion-chip"
            onClick={() => onSuggestionClick?.(s)}
          >
            {s}
          </button>
        ))}
      </div>
    ) : null

    return (
      <div
        ref={directiveSurface ? directiveSurfaceRef : undefined}
        className={`orca-console-container${directiveSurface ? " orca-directive-surface" : ""}${directiveSurface && directiveLaneCompact ? " is-lane-compact" : ""}`}
        data-orca-tier={MODE_TIER[orcaMode] ?? 0}
        {...(directiveSurface ? { role: "region", "aria-label": "ORCA Directive Surface" } : {})}
      >
        {directiveSurface && isMobile && (
          <p className="orca-directive-greeting">
            {getTimeOfDayGreeting()}{userName ? `, ${userName}` : ""}
          </p>
        )}

        {/* Greeting */}
        {!directiveSurface && (
          <div className="orca-greeting-row flex items-center justify-center gap-3 mb-6">
            <img
              src={labsLogo}
              alt="44 Laboratories"
              className="w-9 h-9 object-contain"
              style={{ filter: "var(--orca-logo-filter, none)" }}
            />
            <span
              className="text-[clamp(28px,3.5vw,42px)] tracking-tight"
              style={{ fontFamily: "var(--orca-font-display)" }}
            >
              Hello, {userName || 'there'}
            </span>
          </div>
        )}

        {directiveSurface ? (
          <>
            {promptCard}
            {standaloneDirectiveText && (
              <section
                className="bearing-home-directive-output"
                aria-label="Current Orca Directive"
              >
                <p className="bearing-home-directive-output-label">ORCA Directive</p>
                <p className="bearing-home-directive-output-text">
                  {standaloneDirectiveText}
                </p>
              </section>
            )}
            {discoveryChoices}
          </>
        ) : (
          <>
            {discoveryChoices}
            {promptCard}
          </>
        )}
      </div>
    )
  }

  // ─── ACTIVE MODE (bottom-anchored, compact action cards) ───
  return (
    <div className="orca-active-input-container">
      {promptCard}
      {actionCards && actionCards.length > 0 && (
        <div className="orca-action-cards compact">
          {actionCards.map((card) => (
            <button
              key={card.id}
              className={`orca-action-card ${(tabChatActive && card.id === "tab_chat") || (artifactActive && card.id === "create") ? "active" : ""}`}
              onClick={() => onActionCardClick?.(card.id)}
            >
              <div className="orca-action-card-title">{card.title}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default ChatInput
